import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Mail, Phone, Calendar, Briefcase, FileText, ChevronLeft, Ban, CheckCircle, AlertCircle,
    User, MapPin, Download, ExternalLink, Clock, Building, GraduationCap, Globe, Eye
} from 'lucide-react';
import api from '../utils/api';

const CandidateDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [candidate, setCandidate] = useState(null);
    const [applications, setApplications] = useState([]);
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [showSuspendModal, setShowSuspendModal] = useState(false);
    const [suspendReason, setSuspendReason] = useState('');
    const [profilePictureUrl, setProfilePictureUrl] = useState(null);

    // Reference Data State
    const [wilayas, setWilayas] = useState([]);
    const [cities, setCities] = useState([]);
    const [languagesList, setLanguagesList] = useState([]);
    const [languageLevels, setLanguageLevels] = useState([]);



    const fetchReferenceData = async () => {
        try {
            const [wilayasRes, langsRes, levelsRes] = await Promise.all([
                api.get('/data/wilayas'),
                api.get('/data/languages'),
                api.get('/data/language-levels')
            ]);
            setWilayas(wilayasRes.data);
            setLanguagesList(langsRes.data);
            setLanguageLevels(levelsRes.data);
        } catch (error) {
            console.error('Error fetching reference data:', error);
        }
    };

    const fetchCities = async (wilayaId) => {
        if (!wilayaId) return;
        try {
            const response = await api.get(`/data/cities/wilaya/${wilayaId}`);
            setCities(response.data);
        } catch (error) {
            console.error('Error fetching cities:', error);
        }
    };

    const fetchCandidate = useCallback(async () => {
        try {
            const response = await api.get(`/admin/candidates/${id}`);
            const candidateData = response.data.candidate;
            setCandidate(candidateData);
            setApplications(response.data.applications);
            setStats(response.data.stats);

            // Fetch cities if wilaya is present
            if (candidateData.address?.wilaya) {
                fetchCities(candidateData.address.wilaya);
            }
        } catch (error) {
            console.error('Error fetching candidate:', error);
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchReferenceData();
        fetchCandidate();
    }, [fetchCandidate]);

    useEffect(() => {
        if (candidate) {
            document.title = `${candidate.first_name || 'Candidat'} ${candidate.last_name || ''} - Admin Tekhdem`;
            if (candidate.address?.wilaya && cities.length === 0) {
                fetchCities(candidate.address.wilaya);
            }
        }
    }, [candidate, cities.length]);

    useEffect(() => {
        const fetchProfilePicture = async () => {
            if (candidate?.profile_picture) {
                try {
                    const urlParts = candidate.profile_picture.split('/');
                    const filename = urlParts.pop();
                    const folder = urlParts.pop(); // Assuming structure .../folder/filename

                    const response = await api.get(`/files/${folder}/${filename}`, {
                        responseType: 'blob'
                    });
                    const imageUrl = URL.createObjectURL(response.data);
                    setProfilePictureUrl(imageUrl);
                } catch (error) {
                    console.error("Error fetching profile picture:", error);
                }
            }
        };

        fetchProfilePicture();

        return () => {
            if (profilePictureUrl) {
                URL.revokeObjectURL(profilePictureUrl);
            }
        };
    }, [candidate?.profile_picture]);

    // Helpers for display
    const getWilayaName = (id) => {
        const wilaya = wilayas.find(w => w.id === String(id) || w.code === String(id));
        return wilaya ? wilaya.name_fr : id;
    };

    const getCityName = (id) => {
        // City ID might be the name itself or an ID depending on storage.
        // Schema says String. If it's a code/ID, we look it up.
        // Assuming ID match.
        const city = cities.find(c => c.id === String(id));
        return city ? city.nom_fr : id; // Assuming communes.json has nom_fr
        // Wait, I haven't seen communes.json. But typically it has `nom` or `nom_fr`.
        // Let's assume `id` and `nom` or `name`.
        // If cities list is empty or finding fails, return original string.
    };

    const getLanguageName = (code) => {
        const lang = languagesList.find(l => l.code === code);
        return lang ? lang.name_fr : code;
    };

    const getLevelName = (code) => {
        const level = languageLevels.find(l => l.code === code);
        return level ? level.name_fr : code;
    };


    const handleSuspend = async () => {
        try {
            await api.put(`/admin/candidates/${id}/suspend`, {
                suspended: !candidate.suspended,
                reason: suspendReason
            });
            fetchCandidate();
            setShowSuspendModal(false);
            setSuspendReason('');
        } catch (error) {
            console.error('Error suspending candidate:', error);
        }
    };

    // View CV in new tab
    const handleViewCV = async (folder, filename) => {
        try {
            const response = await api.get(`/files/${folder}/${filename}`, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');

            // Cleanup
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch (error) {
            console.error("Error viewing CV:", error);
            alert("Erreur lors de l'ouverture du CV");
        }
    };

    // Download CV
    const handleDownloadCV = async (folder, filename) => {
        try {
            const response = await api.get(`/files/${folder}/${filename}`, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading CV:", error);
            alert("Erreur lors du téléchargement du CV");
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen bg-slate-50 dark:bg-slate-900">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-slate-500 font-medium">Chargement du profil...</p>
                </div>
            </div>
        );
    }

    if (!candidate) return <div className="p-8 text-center text-slate-500">Candidat non trouvé</div>;

    const TabButton = ({ id, label, icon: Icon }) => (
        <button
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-6 py-3 font-medium text-sm transition-all border-b-2 ${activeTab === id
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50 dark:hover:text-slate-300 dark:hover:bg-slate-800'
                }`}
        >
            <Icon className="w-4 h-4" />
            {label}
        </button>
    );

    const StatusBadge = ({ status }) => {
        const styles = {
            accepted: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200",
            pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200",
            rejected: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200",
            appointmentscheduled: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200"
        };

        const labels = {
            accepted: "Accepté",
            pending: "En attente",
            rejected: "Refusé",
            appointmentscheduled: "Entretien prévu"
        };

        const icons = {
            accepted: CheckCircle,
            pending: AlertCircle,
            rejected: Ban,
            appointmentscheduled: Calendar
        };

        const Icon = icons[status] || AlertCircle;

        return (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
                <Icon className="w-3.5 h-3.5" />
                {labels[status] || status}
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-20">
            {/* Header / Banner */}
            <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="py-4">
                        <button
                            onClick={() => navigate('/candidates')}
                            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors mb-4 group"
                        >
                            <div className="p-1 rounded-full group-hover:bg-slate-100 dark:group-hover:bg-slate-800 transition-colors">
                                <ChevronLeft className="w-5 h-5" />
                            </div>
                            <span className="font-medium">Retour aux candidats</span>
                        </button>

                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                            <div className="flex items-start gap-6">
                                <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center border-4 border-white dark:border-slate-900 shadow-lg overflow-hidden shrink-0">
                                    {profilePictureUrl ? (
                                        <img src={profilePictureUrl} alt="Profile" className="w-full h-full object-cover" />
                                    ) : (
                                        <User className="w-10 h-10 text-slate-400" />
                                    )}
                                </div>
                                <div>
                                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                                        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                                            {candidate.first_name} {candidate.last_name}
                                        </h1>
                                        {candidate.suspended && (
                                            <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold border border-red-200 uppercase tracking-wide">
                                                Compte Suspendu
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600 dark:text-slate-400">
                                        {candidate.title && (
                                            <div className="flex items-center gap-2">
                                                <Briefcase className="w-4 h-4 text-indigo-500" />
                                                <span className="font-medium text-slate-900 dark:text-slate-200">{candidate.title}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 hover:text-indigo-600 transition-colors">
                                            <Mail className="w-4 h-4" />
                                            <a href={`mailto:${candidate.user_id?.email}`}>{candidate.user_id?.email}</a>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-4 h-4" />
                                            <span>{candidate.phone_number || 'Non renseigné'}</span>
                                        </div>
                                        {candidate.address?.city && (
                                            <div className="flex items-center gap-2">
                                                <MapPin className="w-4 h-4" />
                                                <span>{getCityName(candidate.address.city)}, {getWilayaName(candidate.address.wilaya)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setShowSuspendModal(true)}
                                    className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all shadow-sm flex items-center gap-2 ${candidate.suspended
                                        ? 'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800'
                                        : 'bg-white border border-slate-200 text-red-600 hover:bg-red-50 hover:border-red-200 dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-red-900/20'
                                        }`}
                                >
                                    {candidate.suspended ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                                    {candidate.suspended ? 'Réactiver le compte' : 'Suspendre'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Tabs Navigation */}
                    <div className="flex overflow-x-auto scrollbar-hide -mb-px pt-4">
                        <TabButton id="overview" label="Vue d'ensemble" icon={User} />
                        <TabButton id="applications" label={`Candidatures (${stats?.totalApplications || applications.length})`} icon={Briefcase} />
                        <TabButton id="profile" label="Profil Détaillé" icon={FileText} />
                        <TabButton id="documents" label="Documents & CV" icon={Download} />
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                {activeTab === 'overview' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Stats Cards */}
                        <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { label: 'Candidatures Total', value: stats?.totalApplications, icon: FileText, color: 'indigo' },
                                { label: 'Missions complétées', value: stats?.missionsCompleted, icon: CheckCircle, color: 'emerald' },
                                { label: 'Taux de réponse', value: 'N/A', icon: ExternalLink, color: 'blue' }, // Placeholder
                                { label: 'Date d\'inscription', value: new Date(candidate.user_id?.created_at).toLocaleDateString('fr-FR'), icon: Calendar, color: 'slate' }
                            ].map((stat, i) => (
                                <div key={i} className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between group hover:border-indigo-200 dark:hover:border-indigo-900 transition-colors">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{stat.label}</p>
                                        <p className="text-2xl font-bold text-slate-900 dark:text-white">{stat.value}</p>
                                    </div>
                                    <div className={`p-3 rounded-lg bg-${stat.color}-50 text-${stat.color}-600 dark:bg-${stat.color}-900/20 dark:text-${stat.color}-400`}>
                                        <stat.icon className="w-6 h-6" />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Recent Activity */}
                        <div className="lg:col-span-2 space-y-6">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Dernières candidatures</h3>
                                <button onClick={() => setActiveTab('applications')} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium hover:underline">Voir tout</button>
                            </div>
                            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                                {applications.length > 0 ? (
                                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {applications.slice(0, 5).map((app) => (
                                            <div key={app._id} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors flex items-center justify-between group">
                                                <div className="flex items-start gap-4">
                                                    <div className={`mt-1 p-2 rounded-lg ${app.type === 'mission' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                                                        {app.type === 'mission' ? <ExternalLink className="w-5 h-5" /> : <Briefcase className="w-5 h-5" />}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-slate-900 dark:text-white group-hover:text-indigo-600 transition-colors">
                                                            {app.title}
                                                        </h4>
                                                        <p className="text-sm text-slate-500 flex items-center gap-2">
                                                            <Building className="w-3.5 h-3.5" />
                                                            {app.company_name}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-2">
                                                    <StatusBadge status={app.status} />
                                                    <span className="text-xs text-slate-400 flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {new Date(app.created_at).toLocaleDateString('fr-FR')}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-12 text-center text-slate-500">Aucune candidature récente</div>
                                )}
                            </div>
                        </div>

                        {/* Quick Info Side */}
                        <div className="space-y-6">
                            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-indigo-500" />
                                    Dernier CV
                                </h3>
                                {candidate.cv_url && candidate.cv_url.length > 0 ? (
                                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
                                                <FileText className="w-6 h-6" />
                                            </div>
                                            <div className="overflow-hidden">
                                                <p className="font-medium text-slate-900 dark:text-white truncate">
                                                    {candidate.cv_url[candidate.cv_url.length - 1].url.split('/').pop()}
                                                </p>
                                                <p className="text-xs text-slate-500">Ajouté le {new Date(candidate.cv_url[candidate.cv_url.length - 1].uploadedAt).toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                const cv = candidate.cv_url[candidate.cv_url.length - 1];
                                                const fileName = cv.url.split('/').pop();
                                                const folder = cv.url.split('/').slice(-2, -1)[0];
                                                handleDownloadCV(folder, fileName);
                                            }}
                                            className="mt-3 flex items-center justify-center gap-2 w-full py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                                        >
                                            <Download className="w-4 h-4" />
                                            Télécharger
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                                        Aucun CV disponible
                                    </div>
                                )}
                                <button onClick={() => setActiveTab('documents')} className="w-full text-center text-sm text-indigo-600 hover:underline">
                                    Voir tous les documents
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'applications' && (
                    <div className="space-y-6">
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
                            <Briefcase className="w-6 h-6 text-indigo-500" />
                            Historique complet des candidatures
                        </h3>
                        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                            {applications.length > 0 ? (
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-xs uppercase text-slate-500 font-semibold">
                                        <tr>
                                            <th className="px-6 py-4">Poste / Mission</th>
                                            <th className="px-6 py-4">Entreprise</th>
                                            <th className="px-6 py-4">Type</th>
                                            <th className="px-6 py-4">Date</th>
                                            <th className="px-6 py-4">Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {applications.map((app) => (
                                            <tr key={app._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                                <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">
                                                    {app.title}
                                                </td>
                                                <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                                                    {app.company_name}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${app.type === 'mission' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                                        }`}>
                                                        {app.type === 'mission' ? 'Mission' : 'CDI/CDD'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-slate-500">
                                                    {new Date(app.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <StatusBadge status={app.status} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="p-12 text-center">
                                    <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Briefcase className="w-8 h-8 text-slate-400" />
                                    </div>
                                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-1">Aucune candidature</h3>
                                    <p className="text-slate-500">Ce candidat n'a pas encore postulé.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'profile' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="space-y-6">
                            {/* Experience */}
                            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-4">
                                    <Briefcase className="w-5 h-5 text-indigo-500" />
                                    Expérience Professionnelle
                                </h3>
                                <div className="space-y-8">
                                    {candidate.experiences?.length > 0 ? candidate.experiences.map((exp, i) => (
                                        <div key={i} className="relative pl-6 border-l-2 border-slate-200 dark:border-slate-700 last:pb-0">
                                            <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-white dark:bg-slate-900 border-2 border-indigo-500"></div>
                                            <h4 className="text-base font-bold text-slate-900 dark:text-white">{exp.title}</h4>
                                            <p className="text-sm text-indigo-600 font-medium mb-1">{exp.company}</p>
                                            <p className="text-xs text-slate-400 mb-3 flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {new Date(exp.start_date).toLocaleDateString()} - {exp.is_current ? 'Présent' : new Date(exp.end_date).toLocaleDateString()}
                                            </p>
                                            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                                {exp.description}
                                            </p>
                                        </div>
                                    )) : <p className="text-slate-500 italic">Aucune expérience renseignée</p>}
                                </div>
                            </div>

                            {/* Education */}
                            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-4">
                                    <GraduationCap className="w-5 h-5 text-indigo-500" />
                                    Formation
                                </h3>
                                <div className="space-y-6">
                                    {candidate.education?.length > 0 ? candidate.education.map((edu, i) => (
                                        <div key={i} className="flex gap-4">
                                            <div className="mt-1">
                                                <div className="w-10 h-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 flex items-center justify-center">
                                                    <GraduationCap className="w-5 h-5" />
                                                </div>
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-slate-900 dark:text-white">{edu.degree} en {edu.field}</h4>
                                                <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">{edu.institution}</p>
                                                <p className="text-xs text-slate-500 mt-1">
                                                    {new Date(edu.start_date).getFullYear()} - {edu.is_current ? 'Présent' : new Date(edu.end_date).getFullYear()}
                                                </p>
                                            </div>
                                        </div>
                                    )) : <p className="text-slate-500 italic">Aucune formation renseignée</p>}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {/* Skills */}
                            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-4">
                                    <CheckCircle className="w-5 h-5 text-indigo-500" />
                                    Compétences
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {candidate.skills?.length > 0 ? candidate.skills.map((skill, i) => (
                                        <span key={i} className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium border border-slate-200 dark:border-slate-700">
                                            {skill.name_fr || skill.name}
                                        </span>
                                    )) : <p className="text-slate-500 italic">Aucune compétence renseignée</p>}
                                </div>
                            </div>

                            {/* Languages */}
                            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-4">
                                    <Globe className="w-5 h-5 text-indigo-500" />
                                    Langues
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {candidate.languages?.length > 0 ? candidate.languages.map((lang, i) => (
                                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                            <span className="font-medium text-slate-900 dark:text-white">{getLanguageName(lang.language)}</span>
                                            <span className="text-xs uppercase font-bold text-slate-500 bg-white dark:bg-slate-900 px-2 py-1 rounded shadow-sm">{getLevelName(lang.level)}</span>
                                        </div>
                                    )) : <p className="text-slate-500 italic">Aucune langue renseignée</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'documents' && (
                    <div className="max-w-4xl mx-auto space-y-6">
                        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                                <Download className="w-5 h-5 text-indigo-500" />
                                CV et Documents
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {candidate.cv_url && candidate.cv_url.length > 0 ? candidate.cv_url.map((cv, index) => {
                                    const fileName = cv.url.split('/').pop();
                                    const folder = cv.url.split('/').slice(-2, -1)[0];
                                    return (
                                        <div key={index} className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-indigo-500 dark:hover:border-indigo-500 hover:shadow-md transition-all group bg-white dark:bg-slate-800">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="p-2.5 bg-red-50 text-red-600 rounded-lg">
                                                    <FileText className="w-6 h-6" />
                                                </div>
                                                {index === candidate.cv_url.length - 1 && (
                                                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded uppercase tracking-wide">Actuel</span>
                                                )}
                                            </div>
                                            <h4 className="font-semibold text-slate-900 dark:text-white mb-1 truncate" title={fileName}>{fileName}</h4>
                                            <p className="text-sm text-slate-500 mb-4">Téléchargé le {new Date(cv.uploadedAt).toLocaleDateString()}</p>

                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleViewCV(folder, fileName)}
                                                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-slate-50 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 font-medium rounded-lg transition-colors"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                    Voir
                                                </button>
                                                <button
                                                    onClick={() => handleDownloadCV(folder, fileName)}
                                                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-slate-50 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 font-medium rounded-lg transition-colors"
                                                >
                                                    <Download className="w-4 h-4" />
                                                    Télécharger
                                                </button>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div className="col-span-2 text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
                                        <p className="text-slate-500">Aucun CV n'a été téléchargé par ce candidat.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Suspend Modal */}
            {showSuspendModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md p-6 shadow-2xl scale-100">
                        <div className="mb-6 text-center">
                            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${candidate.suspended ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                {candidate.suspended ? <CheckCircle className="w-8 h-8" /> : <Ban className="w-8 h-8" />}
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                                {candidate.suspended ? 'Réactiver le compte' : 'Suspendre le compte'}
                            </h3>
                            <p className="text-sm text-slate-500">
                                {candidate.suspended
                                    ? 'Êtes-vous sûr de vouloir réactiver ce compte ? Le candidat retrouvera immédiatement l\'accès à la plateforme.'
                                    : 'Êtes-vous sûr de vouloir suspendre ce compte ? Le candidat ne pourra plus se connecter.'}
                            </p>
                        </div>

                        {!candidate.suspended && (
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Motif de la suspension (optionnel)</label>
                                <textarea
                                    value={suspendReason}
                                    onChange={(e) => setSuspendReason(e.target.value)}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-slate-800 dark:text-white"
                                    placeholder="Ex: Non respect des conditions d'utilisation..."
                                />
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowSuspendModal(false)}
                                className="flex-1 px-4 py-2.5 border border-slate-300 dark:border-slate-700 rounded-lg font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSuspend}
                                className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-white shadow-lg shadow-red-500/20 transition-all ${candidate.suspended
                                    ? 'bg-emerald-600 hover:bg-emerald-700'
                                    : 'bg-red-600 hover:bg-red-700'
                                    }`}
                            >
                                {candidate.suspended ? 'Confirmer la réactivation' : 'Confirmer la suspension'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CandidateDetails;
